import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useLocation } from 'react-router-dom';

const SEOHead = ({ 
  title, 
  description, 
  keywords,
  ogImage = "https://audiosum.ai/audiosum-social.png",
  structuredData 
}) => {
  const { lang } = useParams();
  const location = useLocation();
  
  // Get current URL without language prefix
  const currentPath = location.pathname.replace(`/${lang}`, '') || '/';
  const baseUrl = "https://audiosum.ai";
  const currentLang = lang || 'en';
  
  // Generate URLs for both language versions
  const enUrl = `${baseUrl}/en${currentPath === '/' ? '' : currentPath}`;
  const arUrl = `${baseUrl}/ar${currentPath === '/' ? '' : currentPath}`;
  
  // Canonical should always point to English version (primary/master for SEO)
  // This consolidates ranking signals and prevents split authority
  const canonicalUrl = enUrl;
  
  // Single source of truth - English-only SEO content
  const defaultContent = {
    title: "AI Summary, Document, Audio Summary & Video Transcription",
    description: "Convert audio and video to text with 99%+ accuracy in 95+ languages. AI-powered summary and transcription made easy with AI summary tool.",
    keywords: "audio transcription, AI transcription, speech to text, audio summarization, AI audio analysis"
  };

  const seoContent = {
    title: title || defaultContent.title,
    description: description || defaultContent.description,
    keywords: keywords || defaultContent.keywords
  };

  // Default structured data
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Audiosum",
    "applicationCategory": "Productivity",
    "operatingSystem": "Web",
    "description": "AI-powered audio transcription and summarization tool supporting Arabic and English languages",
    "url": canonicalUrl,
    "image": "https://audiosum.ai/logo.png",
    "logo": "https://audiosum.ai/logo.png",
    "author": {
      "@type": "Organization",
      "name": "Audiosum AI",
      "logo": "https://audiosum.ai/logo.png"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free trial available"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Sarah Mitchell" },
        "datePublished": "2024-10-01",
        "reviewBody": "Audiosum has completely transformed how I handle podcast transcription. The accuracy is remarkable and the Arabic language support is excellent. Highly recommended!",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1" }
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Mohammed Al-Zahrani" },
        "datePublished": "2024-09-28",
        "reviewBody": "أداة رائعة جداً للتفريغ الصوتي. دقة عالية في اللهجة الخليجية والدعم الفني ممتاز. أنصح بها بشدة للمحترفين.",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1" }
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Jennifer Chen" },
        "datePublished": "2024-09-25",
        "reviewBody": "Perfect for transcribing university lectures. The AI summarization feature saves me hours of work. Great for students and researchers.",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1" }
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Ahmed Al-Rashid" },
        "datePublished": "2024-09-20",
        "reviewBody": "خدمة ممتازة لتحويل الاجتماعات والمقابلات إلى نص. الدقة عالية جداً والسرعة مذهلة. يدعم اللغة العربية بشكل احترافي.",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1" }
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "David Thompson" },
        "datePublished": "2024-09-15",
        "reviewBody": "Excellent tool for meeting transcription. The multiple language support and speaker identification features are outstanding. Worth every penny!",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1" }
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Fatima Al-Kuwari" },
        "datePublished": "2024-09-10",
        "reviewBody": "أفضل أداة استخدمتها لتفريغ المحاضرات والبودكاست. دقة التفريغ ممتازة والتلخيص الذكي يوفر الكثير من الوقت.",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1" }
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Michael Rodriguez" },
        "datePublished": "2024-09-05",
        "reviewBody": "Great accuracy and fast processing. The Gulf dialect recognition is impressive. Very useful for business professionals working with Arabic content.",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1" }
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Layla Hassan" },
        "datePublished": "2024-08-28",
        "reviewBody": "خدمة احترافية للتفريغ الصوتي. استخدمها يومياً في عملي ولم تخذلني أبداً. الدعم الفني سريع ومفيد.",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1" }
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Robert Kim" },
        "datePublished": "2024-08-20",
        "reviewBody": "Solid transcription platform with excellent multilingual support. The interface is user-friendly and processing is quick. Minor issues with very noisy audio.",
        "reviewRating": { "@type": "Rating", "ratingValue": "4", "bestRating": "5", "worstRating": "1" }
      },
      {
        "@type": "Review",
        "author": { "@type": "Person", "name": "Nora Al-Mutairi" },
        "datePublished": "2024-08-15",
        "reviewBody": "أداة ممتازة للباحثين والطلاب. تفريغ دقيق وسريع للمحاضرات والمقابلات. أنصح بها بشدة لكل من يحتاج تفريغ صوتي احترافي.",
        "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5", "worstRating": "1" }
      }
    ],
    "featureList": [
      "AI Audio Transcription",
      "Arabic Language Support",
      "Gulf Dialect Recognition",
      "Podcast Transcription",
      "Meeting Notes Generation",
      "Audio Summarization",
      "Multiple File Processing",
      "95+ Language Support"
    ],
    "inLanguage": ["en", "ar"]
  };

  return (
    <Helmet>
      {/* Basic meta tags */}
      <html lang={currentLang} />
      <title>{seoContent.title}</title>
      <meta name="description" content={seoContent.description} />
      <meta name="keywords" content={seoContent.keywords} />
      
      {/* Canonical URL - self-referencing (points to current page) */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang tags - for bilingual support (English + Arabic) */}
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="ar" href={arUrl} />
      <link rel="alternate" hrefLang="x-default" href={enUrl} />
      
      {/* Open Graph tags */}
      <meta property="og:title" content={seoContent.title} />
      <meta property="og:description" content={seoContent.description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Audiosum AI" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoContent.title} />
      <meta name="twitter:description" content={seoContent.description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="Audiosum AI - Convert audio to text with AI-powered transcription" />
      <meta name="twitter:site" content="@AudiosumAI" />
      <meta name="twitter:creator" content="@AudiosumAI" />
      
      {/* Additional SEO meta tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Audiosum AI" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#000000" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;